import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { IUsuarioDropdownItem, ICapturistaListItem, ICapturistaDetailRequest } from './interfaces/capturistas.interface';
import { CapturistasService } from './services/capturistas.service';
import { MatDialog } from '@angular/material/dialog';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-capturista-form',
  templateUrl: './capturista-form.component.html',
  providers: [MessageService]
})
export class CapturistaFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() model: ICapturistaListItem | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;
  usuariosDropdown: IUsuarioDropdownItem[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private capturistasService: CapturistasService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadUsuariosDropdown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      if (changes['model'].currentValue) {
        this.loadCapturistaDetail(this.model.susuario);
      } else {
        this.initForm();
        this.form.reset();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.form = this.fb.group({
      clave_capturista: [null, [Validators.required]],
      nombre: [{ value: '', disabled: true }, [Validators.required]]
    });

    // Suscribirse a cambios en el campo clave_capturista
    this.form.get('clave_capturista')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.onUsuarioSelected(value);
        }
      });
  }

  onUsuarioSelected(susuario: string): void {
    const selectedUser = this.usuariosDropdown.find(user => user.susuario === susuario);
    if (selectedUser) {
      this.form.patchValue({
        nombre: selectedUser.nombreusuario
      });
    }
  }

  loadCapturistaDetail(susuario: string): void {
    this.loading = true;
    const payload: ICapturistaDetailRequest = {
      que: 5,
      tope: '',
      cmd: '',
      clave_capturista: susuario,
      nombre: ''
    };
    this.capturistasService.getCapturistaDetail(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data.length > 0) {
            const detalle = response.data[0];
            this.form.patchValue({
              clave_capturista: detalle.susuario,
              nombre: detalle.nombre
            });
            this.form.get('clave_capturista')?.disable();
            this.form.get('nombre')?.disable();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudo cargar el detalle del capturista'
            });
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al cargar detalle de capturista:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar el detalle del capturista'
          });
          this.loading = false;
        }
      });
  }

  loadUsuariosDropdown(): void {
    this.capturistasService.getUsuariosDropdown({ tope: '9999', usuario: '' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.usuariosDropdown = response.data;
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'No se pudieron cargar los usuarios'
            });
          }
        },
        error: (error) => {
          console.error('Error al cargar usuarios para el dropdown:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los usuarios'
          });
        }
      });
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload = {
      que: 1,
      tope: '',
      cmd: '',
      clave_capturista: formData.clave_capturista,
      nombre: formData.nombre
    };

    this.capturistasService.addCapturista(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({
            success: true,
            message: 'Capturista creado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear el capturista'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al crear capturista:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear el capturista'
        });
        this.loading = false;
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload = {
      que: 2,
      tope: '',
      cmd: '',
      clave_capturista: this.model.susuario,
      nombre: formData.nombre
    };

    this.capturistasService.updateCapturista(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({
            success: true,
            message: 'Capturista actualizado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar el capturista'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al actualizar capturista:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el capturista'
        });
        this.loading = false;
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'capturistas',
        sClave: `${this.form.get('clave_capturista')?.value}`,
        sNombre: this.form.get('nombre')?.value,
        sTipo: 'Opcion'
      }
    });
  }

  onDialogClosed(): void {
    this.form.reset();
    this.model = null;
  }
} 